/**
 * Defaults de TanStack Query. **Una vista nueva no debería tener que tocarlos.**
 *
 * Las tres decisiones que importan:
 *
 * 1. **`retry` mira el `code`, no cuenta intentos a ciegas.** Reintentar un
 *    403 tres veces no lo convierte en 200: sólo agrega 600 ms al mensaje de
 *    error y tres líneas de ruido al log. Se reintenta lo que puede mejorar
 *    solo: red, timeout, 429 y 5xx.
 *
 * 2. **`staleTime` 30 s.** El SLO de dashboard operativo es p95 < 800 ms; con
 *    `staleTime: 0` cada navegación entre módulos vuelve a pedir todo y el
 *    dashboard se siente lento aunque la API responda bien. 30 s es el
 *    compromiso: moverse entre pestañas es instantáneo y el dato nunca tiene
 *    más de medio minuto. Un módulo que necesite otra cosa (la bandeja de
 *    alertas quiere menos, el catálogo de productos quiere mucho más) lo pisa
 *    en su propia query — el default está para no tener que pensarlo.
 *
 * 3. **`throwOnError: false`.** Los errores se **renderizan**, no se lanzan a
 *    un error boundary. Un dashboard que reemplaza toda la pantalla por
 *    "algo salió mal" porque falló un contador es peor que uno que muestra el
 *    contador roto y el resto funcionando.
 */
import { QueryCache, QueryClient } from '@tanstack/react-query';

import { asApiError } from './errors';

const MAX_RETRIES = 2;

export interface QueryClientDeps {
  /** Se llama con cada error de query. Sirve para telemetría o toasts. */
  onQueryError?: (error: unknown) => void;
}

export function createQueryClient(deps: QueryClientDeps = {}): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => deps.onQueryError?.(error),
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          if (failureCount >= MAX_RETRIES) return false;
          return asApiError(error).isRetriable;
        },
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
        // El operador vuelve a la pestaña después de una reunión: que vea
        // datos frescos sin apretar nada. Con `staleTime` no genera tormenta.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        throwOnError: false,
      },
      mutations: {
        // Una mutación no se reintenta sola: reintentar un "retirar lote"
        // que en realidad sí se aplicó lo aplicaría dos veces.
        retry: false,
        throwOnError: false,
      },
    },
  });
}
